import { Box, styled, useTheme, type Breakpoint, type SxProps } from "@mui/material";

const ORDERED_BREAKPOINTS: Breakpoint[] = ["sm", "md", "lg", "xl"];

const GridContainer = styled(Box)<{
  $containerName: string | undefined;
}>`
  container-type: inline-size;
  container-name: ${({ $containerName }) => ($containerName ? $containerName : undefined)};
  display: grid;
  grid-template-columns: repeat(12, 1fr);
`;

const GridItem = styled(Box)``;

interface Props {
  container?: boolean;
  containerName?: string;
  size?: Partial<Record<Breakpoint, number>> | number;
  spacing?: Partial<Record<Breakpoint, number>> | number;
  offset?: Partial<Record<Breakpoint, number>> | number;
  children?: React.ReactNode;
  sx?: SxProps;
}

const ContainerGrid = (props: Props) => {
  const theme = useTheme();

  function getOffset(offset: Partial<Record<Breakpoint, number>> | number | undefined, breakpoint?: Breakpoint) {
    let result: number | undefined = undefined;

    if (typeof offset === "number") {
      result = offset;
    } else if (offset) {
      if (breakpoint) {
        result = offset[breakpoint];
      } else {
        result = offset["xs"];
      }
    }

    return result === 0 ? undefined : result;
  }

  if (props.container) {
    const mediaQueries: Record<string, object> = {};
    let defaultGap: number | undefined = undefined;

    if (typeof props.spacing === "number") {
      defaultGap = props.spacing;
    } else if (props.spacing) {
      for (const breakpoint of ORDERED_BREAKPOINTS) {
        const cols = props.spacing[breakpoint];

        if (cols !== undefined) {
          mediaQueries[theme.breakpoints.up(breakpoint)] = {
            gap: props.spacing[breakpoint],
          };
        }
      }

      if (props.spacing.xs !== undefined) {
        defaultGap = props.spacing.xs;
      }
    }

    return (
      <GridContainer
        $containerName={props.containerName}
        sx={{
          gap: defaultGap,
          ...mediaQueries,
          ...props.sx,
        }}
      >
        {props.children}
      </GridContainer>
    );
  }

  const containerQueries: Record<string, object> = {};
  let defaultGridColumn = "span 12";

  if (typeof props.size === "number") {
    const offset = getOffset(props.offset);

    if (offset !== undefined) {
      defaultGridColumn = `${offset + 1} / span ${props.size}`;
    } else {
      defaultGridColumn = `span ${props.size}`;
    }
  } else if (props.size) {
    for (const breakpoint of ORDERED_BREAKPOINTS) {
      const cols = props.size[breakpoint];
      const offset = getOffset(props.offset, breakpoint);

      if (cols !== undefined) {
        if (offset !== undefined) {
          containerQueries[theme.containerQueries.up(breakpoint)] = {
            gridColumn: `${offset + 1} / span ${cols}`,
          };
        } else {
          containerQueries[theme.containerQueries.up(breakpoint)] = {
            gridColumn: `span ${cols}`,
          };
        }
      }
    }

    // If there's an xs size set, use it as the default.
    if (props.size.xs !== undefined) {
      const offset = getOffset(props.offset, "xs");

      if (offset !== undefined) {
        defaultGridColumn = `${offset + 1} / span ${props.size.xs}`;
      } else {
        defaultGridColumn = `span ${props.size.xs}`;
      }
    }
  }

  return (
    <GridItem
      sx={{
        gridColumn: defaultGridColumn,
        ...containerQueries,
        ...props.sx,
      }}
    >
      {props.children}
    </GridItem>
  );
};

export { ContainerGrid };
